import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { providerBookingApi } from '../api/providerBookingApi';
import { connectSocket, getSocket } from '../socket/socketClient';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (number) => String(number).padStart(2, '0');
const ymd = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;
const statusClass = (status) => status === 'in_progress' ? 'bg-[#185FA5]' : 'bg-giggs-green';
const statusTextClass = (status) => status === 'in_progress' ? 'text-[#185FA5]' : 'text-giggs-green';

const ProviderJobsCalendar = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selected, setSelected] = useState(ymd(today.getFullYear(), today.getMonth(), today.getDate()));

  const load = useCallback(async () => {
    try {
      const response = await providerBookingApi.list('active');
      setJobs(response.data || []);
    } catch {
      // Keep the schedule empty when the request fails.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const socket = getSocket() || connectSocket();
    if (!socket) return undefined;
    const refresh = () => load();
    socket.on('booking:new', refresh);
    socket.on('booking:updated', refresh);
    return () => {
      socket.off('booking:new', refresh);
      socket.off('booking:updated', refresh);
    };
  }, [load]);

  const byDate = useMemo(() => {
    const map = {};
    jobs.forEach((job) => {
      if (!job.dateKey) return;
      (map[job.dateKey] = map[job.dateKey] || []).push(job);
    });
    return map;
  }, [jobs]);

  const firstDow = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const prevMonth = () => setView((current) => current.month === 0 ? { year: current.year - 1, month: 11 } : { year: current.year, month: current.month - 1 });
  const nextMonth = () => setView((current) => current.month === 11 ? { year: current.year + 1, month: 0 } : { year: current.year, month: current.month + 1 });

  const selectedJobs = byDate[selected] || [];
  const fmtSelected = (() => {
    const [year, month, day] = selected.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
  })();

  return (
    <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-3.5 flex items-center justify-between gap-4">
        <h3 className="m-0 font-nunito text-base font-bold text-[#10271d]">Your schedule</h3>
        <button type="button" onClick={() => navigate('/provider/bookings')} className="cursor-pointer border-0 bg-transparent p-0 text-[13px] font-semibold text-giggs-green transition-colors hover:text-giggs-green-hover">
          All bookings
        </button>
      </div>

      {loading ? (
        <div className="px-4 py-[30px] text-center text-giggs-muted">Loading schedule…</div>
      ) : (
        <>
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <button type="button" onClick={prevMonth} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-[#E3EAE6] bg-white text-base text-[#41584d]">‹</button>
            <strong className="text-sm text-[#10271d] sm:text-base">{MONTHS[view.month]} {view.year}</strong>
            <button type="button" onClick={nextMonth} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-[#E3EAE6] bg-white text-base text-[#41584d]">›</button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((day) => <div key={day} className="py-1 text-center text-[11px] font-semibold text-giggs-muted">{day}</div>)}
            {Array.from({ length: firstDow }).map((_, index) => <div key={`blank-${index}`} />)}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const dayNum = index + 1;
              const key = ymd(view.year, view.month, dayNum);
              const dayJobs = byDate[key] || [];
              const hasJobs = dayJobs.length > 0;
              const isSelected = selected === key;
              const isToday = view.year === today.getFullYear() && view.month === today.getMonth() && dayNum === today.getDate();

              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setSelected(key)}
                  className={`relative aspect-square min-w-0 cursor-pointer rounded-lg text-[clamp(0.65rem,2.5vw,0.8125rem)] font-semibold transition-colors ${
                    isSelected
                      ? 'border-2 border-giggs-green'
                      : isToday
                        ? 'border border-[#C5D2CB]'
                        : 'border border-[#EEF2EF]'
                  } ${hasJobs ? 'bg-giggs-green/[0.08] text-[#10271d]' : 'bg-white text-giggs-muted'}`}
                >
                  {dayNum}
                  {hasJobs && <span className={`absolute bottom-1 left-1/2 h-[5px] w-[5px] -translate-x-1/2 rounded-full ${statusClass(dayJobs[0].status)}`} />}
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <div className="mb-2 text-[13px] font-bold text-[#10271d]">{fmtSelected}</div>
            {selectedJobs.length === 0 ? (
              <div className="text-[13px] text-giggs-muted">No confirmed jobs this day.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {[...selectedJobs]
                  .sort((a, b) => a.startTime.localeCompare(b.startTime))
                  .map((job) => (
                    <button
                      type="button"
                      key={job._id}
                      onClick={() => navigate('/provider/bookings')}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-[10px] border border-[#E3EAE6] bg-white px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className={`h-9 w-1 shrink-0 rounded-sm ${statusClass(job.status)}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-[#10271d]">{job.service?.name}</span>
                        <span className="block truncate text-xs text-giggs-muted">{job.startTime}–{job.endTime} · {job.clientName} · {job.location?.suburb}</span>
                      </span>
                      <span className={`shrink-0 text-[11px] font-bold ${statusTextClass(job.status)}`}>
                        {job.status === 'in_progress' ? 'In progress' : 'Accepted'}
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ProviderJobsCalendar;
